import React, { useState, useEffect } from "react";
import axios from 'axios';
import './SaleOrder.css';

const EditSaleForm = ({ saleOrderId, onSaleOrderUpdated, onCancel, currentUserFullName }) => {
    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const [formData, setFormData] = useState({
        ref_no: '',
        sale_order_date: '',
        expired_date: '',
        chassis_number: '',
        client_id: '',
        country: '',
        incoterm: 'C&F',
        shipment_type: 'RORO',
        discount_type: 'No Discount',
        status: 'Draft',
        currency: 'USD',
        bill_type: 'Collect',
        consignee: '',
        shipping_port: '',
        description: '',
        assigned_to_type: 'everyone',
        group_id: 0,
        reporter: currentUserFullName || 'Super Admin',
        sub_total: '0.00',
        discount: '0.00',
        adjustment: '0.00',
        total: '0.00',
        stock_name: '',
        stock_id: null,
        mode: 'edit'
    });

    const [clientsData, setClientsData] = useState([]);
    const [stocksData, setStocksData] = useState([]);
    const [groupsData, setGroupsData] = useState({ administrators: [], groups: [] });
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [permissions, setPermissions] = useState({});
    const [items, setItems] = useState([{ stock_id: '', item_name: '', description: '', qty: 1, unit_type: 'Unit Type', unit_price: '', tax_rate: 'No Tax', total: '0.00' }]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // ===== LOAD CLIENTS, STOCKS, THEN THE SALE ORDER =====
    useEffect(() => {
        const boot = async () => {
            await fetchClients();
            await fetchStocks();
            await loadSaleOrderForEdit(saleOrderId);
            setLoading(false);
        };
        if (saleOrderId) boot();
    }, [saleOrderId]);

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_clients.php', { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) setClientsData(res.data.data);
        } catch (err) { console.error(err); }
    };

    const fetchStocks = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_available_stock.php', { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) setStocksData(res.data.data);
        } catch (err) { console.error(err); }
    };

    const loadSaleOrderForEdit = async (id) => {
        try {
            const token = localStorage.getItem("token");

            const res = await axios.get(`https://skcapi.btcjapan.co/get_single_sale_order.php?id=${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.data.success) return;

            const data = res.data.data;
            setFormData({
                ref_no: data.ref_no || '',
                sale_order_date: data.sale_order_date || '',
                expired_date: data.expired_date || '',
                chassis_number: data.chassis_number || '',
                client_id: data.client_id || '',
                country: data.country || '',
                incoterm: data.incoterm || 'C&F',
                shipment_type: data.shipment_type || 'RORO',
                discount_type: data.discount_type || 'No Discount',
                status: data.status || 'Draft',
                currency: data.currency || 'USD',
                bill_type: data.bill_type || 'Collect',
                consignee: data.consignee || '',
                shipping_port: data.shipping_port || '',
                description: data.description || '',
                assigned_to_type: data.assigned_to_type || 'everyone',
                group_id: data.group_id || 0,
                reporter: data.reporter || currentUserFullName || 'Super Admin',
                sub_total: data.sub_total || '0.00',
                discount: data.discount || '0.00',
                adjustment: data.adjustment || '0.00',
                total: data.total || '0.00',
                stock_name: data.stock_name || '',
                stock_id: data.stock_id || null
            });

            if (data.items && data.items.length > 0) setItems(data.items);
            if (data.group_id) setSelectedGroupId(String(data.group_id));

            // Load groups BEFORE mapping permissions
            const groupsRes = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (groupsRes.data.success) {
                setGroupsData(groupsRes.data);

                if (data.assigned_to_type === 'customize' && data.permissions) {
                    const permObj = {};
                    data.permissions.forEach(p => {
                        permObj[p.user_id] = {
                            can_view: Number(p.can_view) === 1,
                            can_edit: Number(p.can_edit) === 1,
                            can_delete: Number(p.can_delete) === 1,
                            is_auto: false
                        };
                    });
                    setPermissions(permObj);
                }
            }
        } catch (err) {
            console.error("loadSaleOrderForEdit error:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'chassis_number' && value) {
            const selectedStock = stocksData.find(s => s.chassis_number === value);
            setItems(prevItems => {
                const updatedItems = [...prevItems];
                if (updatedItems.length > 0) {
                    updatedItems[0] = {
                        ...updatedItems[0],
                        stock_id: selectedStock ? selectedStock.id : updatedItems[0].stock_id,
                        item_name: value,
                        description: selectedStock ? selectedStock.stock_name : ''
                    };
                }
                return updatedItems;
            });
        }

        if (name === 'assigned_to_type' && value === 'everyone') setSelectedGroupId('');
    };

    const handleGroupChange = (e) => {
        const groupId = e.target.value;
        setSelectedGroupId(groupId);
        if (!groupId) { setPermissions({}); return; }
        const group = groupsData.groups.find(g => g.group_id == groupId);
        if (!group) return;
        const newPermissions = {};
        groupsData.administrators.forEach(admin => { newPermissions[admin.id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true }; });
        if (group.leader_id) newPermissions[group.leader_id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        setPermissions(prev => {
            const merged = { ...newPermissions };
            Object.keys(prev).forEach(uid => { if (!merged[uid] && !prev[uid].is_auto) merged[uid] = prev[uid]; });
            return merged;
        });
    };

    const toggleMember = (userId, checked) => {
        if (checked) setPermissions(prev => ({ ...prev, [userId]: { can_view: true, can_edit: false, can_delete: false, is_auto: false } }));
        else setPermissions(prev => { const copy = { ...prev }; if (copy[userId]?.is_auto) return prev; delete copy[userId]; return copy; });
    };

    const handlePermissionChange = (userId, permType, checked) => {
        setPermissions(prev => {
            const current = prev[userId] || { can_view: false, can_edit: false, can_delete: false };
            return { ...prev, [userId]: { ...current, [permType]: checked } };
        });
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], [field]: value };

        if (field === 'item_name') {
            const selectedStock = stocksData.find(s => s.chassis_number === value);
            if (selectedStock) {
                updatedItems[index].stock_id = selectedStock.id;
                updatedItems[index].description = selectedStock.stock_name;
            }
        }

        const qty = parseFloat(updatedItems[index].qty) || 0;
        const price = parseFloat(updatedItems[index].unit_price) || 0;
        updatedItems[index].total = (qty * price).toFixed(2);
        setItems(updatedItems);
        calculateTotals(updatedItems);
    };

    const addItem = () => {
        setItems([...items, { stock_id: '', item_name: '', description: '', qty: 1, unit_type: 'Unit Type', unit_price: '', tax_rate: 'No Tax', total: '0.00' }]);
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            const updatedItems = items.filter((_, i) => i !== index);
            setItems(updatedItems);
            calculateTotals(updatedItems);
        }
    };

    const calculateTotals = (updatedItems) => {
        let subTotal = 0;
        updatedItems.forEach(item => { subTotal += parseFloat(item.total) || 0; });
        const discount = parseFloat(formData.discount) || 0;
        const adjustment = parseFloat(formData.adjustment) || 0;
        const total = subTotal - discount + adjustment;
        setFormData(prev => ({ ...prev, sub_total: subTotal.toFixed(2), total: total.toFixed(2) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.chassis_number) {
            alert("Chassis Number is required.");
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            let permissionsPayload = [];
            if (formData.assigned_to_type === 'customize') {
                if (!selectedGroupId) {
                    alert("Please select a group");
                    setSaving(false);
                    return;
                }
                permissionsPayload = Object.entries(permissions).map(([userId, perms]) => ({
                    user_id: parseInt(userId, 10),
                    can_view: perms.can_view ? 1 : 0,
                    can_edit: perms.can_edit ? 1 : 0,
                    can_delete: perms.can_delete ? 1 : 0
                }));
            }

            const selectedStock = stocksData.find(s => s.chassis_number === formData.chassis_number);

            const sanitizedItems = items.map(it => ({
                ...it,
                stock_id: it.stock_id ? parseInt(it.stock_id, 10) : null,
                unit_price: it.unit_price !== '' && !isNaN(it.unit_price) ? it.unit_price : '0.00',
                qty: it.qty !== '' ? parseInt(it.qty, 10) : 1,
                total: it.total !== '' && !isNaN(it.total) ? it.total : '0.00'
            }));

            const payload = {
                ...formData,
                mode: 'edit',              // 👈 ADD THIS
                id: saleOrderId,
                stock_name: selectedStock ? selectedStock.stock_name : formData.stock_name,
                stock_id: selectedStock ? selectedStock.id : formData.stock_id,
                group_id: selectedGroupId ? parseInt(selectedGroupId, 10) : null,
                permissions: permissionsPayload,
                items: sanitizedItems
            };

            const res = await axios.post(
                'https://skcapi.btcjapan.co/create_update_sale_order.php',
                payload,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );

            if (res.data.success) {
                alert('Sale Order updated successfully!');
                if (onSaleOrderUpdated) onSaleOrderUpdated();
            } else {
                alert("Failed: " + (res.data.error || res.data.message));
            }
        } catch (err) {
            console.error(err);
            const serverMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            alert("Error saving Sale Order: " + serverMsg);
        } finally {
            setSaving(false);
        }
    };

    let displayGroups = [];
    if (userType === 'administrator') displayGroups = groupsData.groups;
    else if (userType === 'leader') displayGroups = groupsData.groups.filter(g => String(g.leader_id) === String(loggedInUserId));
    else if (userType === 'member') displayGroups = groupsData.groups.filter(group => group.members.some(m => String(m.id) === String(loggedInUserId)));

    const selectedGroup = displayGroups.find(g => g.group_id == selectedGroupId);

    if (loading) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading Sale Order...</div>;
    }

    return (
        <div className="new-sale-form-container sale-order-drawer-form">
            <form onSubmit={handleSubmit} className="sale-order-form">

                <div className="sale-form-hero">
                    <div>
                        <div className="sale-form-eyebrow">
                            <span className="eyebrow-dot"></span>
                            EDIT SALES DOCUMENT
                            <span className="ref-chip">{formData.ref_no}</span>
                        </div>
                        <h2>Edit Sale Order & Commercial Invoice</h2>
                        <p>Update the customer & shipping information below.</p>
                    </div>
                    <div className="sale-form-hero-actions">
                        <div className="document-status-control">
                            <span>Document Status:</span>
                            <select name="status" value={formData.status} onChange={handleChange}>
                                <option>Draft</option><option>Sent</option><option>Open</option><option>Revised</option><option>Decline</option><option>Accepted</option>
                            </select>
                        </div>
                        <button type="submit" className="btn-blue hero-save-btn" disabled={saving}>{saving ? 'Saving...' : 'Update Sale Order'}</button>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">📦</div>
                            <div>
                                <h3>Customer & Shipping Information</h3>
                                <p>Update the shipping details for this order.</p>
                            </div>
                        </div>
                    </div>

                    <div className="form-fields-grid form-fields-grid-3">
                        <div className="form-group">
                            <label>Client <span>*</span></label>
                            <select name="client_id" value={formData.client_id} onChange={handleChange}>
                                <option value="">-- Select Client --</option>
                                {clientsData.map(client => <option key={client.id} value={client.id}>{client.client_name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Destination Country <span>*</span></label>
                            <select name="country" value={formData.country} onChange={handleChange}>
                                <option value="">-- Select Country --</option>
                                {clientsData.map(client => <option key={client.id} value={client.id}>{client.client_country}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Consignee</label>
                            <input type="text" name="consignee" value={formData.consignee} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Port of Discharge</label>
                            <input type="text" name="shipping_port" value={formData.shipping_port} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Sale Order Date</label>
                            <input type="date" name="sale_order_date" value={formData.sale_order_date} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Validity Expiry Date</label>
                            <input type="date" name="expired_date" value={formData.expired_date} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Incoterm</label>
                            <select name="incoterm" value={formData.incoterm} onChange={handleChange}>
                                <option>C&F (Cost & Freight)</option><option>C&I</option><option>CIF</option><option>FOB</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Shipment Type</label>
                            <select name="shipment_type" value={formData.shipment_type} onChange={handleChange}>
                                <option>RORO (Roll-on/Roll-off)</option><option>Container</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Currency</label>
                            <select name="currency" value={formData.currency} onChange={handleChange}>
                                <option>USD ($)</option><option>YEN (¥)</option><option>EUR (€)</option><option>GBP (£)</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Bill Type</label>
                            <select name="bill_type" value={formData.bill_type} onChange={handleChange}>
                                <option>Collect</option><option>Prepaid</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Discount Type</label>
                            <select name="discount_type" value={formData.discount_type} onChange={handleChange}>
                                <option>No Discount</option><option>Before Tax</option><option>After Tax</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-section-card permission-section-card">
                    <div className="section-card-heading">
                        <div className="section-title-wrap">
                            <div className="section-icon">🔐</div>
                            <div>
                                <h3>Permission Access</h3>
                                <p>Assign this document to specific users or groups.</p>
                            </div>
                        </div>
                        <div className="assigned-radio-group">
                            <label className="assigned-radio-option permission-choice">
                                <input type="radio" name="assigned_to_type" value="everyone" checked={formData.assigned_to_type === 'everyone'} onChange={handleChange} />
                                <span><strong>Everyone</strong><small>All users can view</small></span>
                            </label>
                            {(userType === 'administrator' || userType === 'leader') && (
                                <label className="assigned-radio-option permission-choice">
                                    <input type="radio" name="assigned_to_type" value="customize" checked={formData.assigned_to_type === 'customize'} onChange={handleChange} />
                                    <span><strong>Customize Permission</strong><small>Assign to group</small></span>
                                </label>
                            )}
                        </div>
                    </div>

                    {formData.assigned_to_type === 'customize' && (
                        <div className="permission-panel">
                            <div className="group-select-block">
                                <label>Select Group <span>*</span></label>
                                <select value={selectedGroupId} onChange={handleGroupChange}>
                                    <option value="">-- Select a Group --</option>
                                    {displayGroups.map(g => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
                                </select>
                            </div>

                            {selectedGroup && (
                                <div className="permission-scroll">
                                    <div className="role-block">
                                        <p className="role-block-title admin">Administrators <span>(Auto)</span></p>
                                        {groupsData.administrators.map(admin => (
                                            <div key={admin.id} className="role-card admin">
                                                <strong>{admin.fname} {admin.lname}</strong>
                                                <span className="role-card-tag">(Administrator)</span>
                                                <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="role-block">
                                        <p className="role-block-title leader">Group Leader <span>(Auto)</span></p>
                                        <div className="role-card leader">
                                            <strong>{selectedGroup.leader_name}</strong>
                                            <span className="role-card-tag">(Leader)</span>
                                            <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                        </div>
                                    </div>

                                    <div className="role-block">
                                        <p className="role-block-title member">Group Members</p>
                                        {selectedGroup.members.filter(m => m.id != selectedGroup.leader_id).map(member => {
                                            const isSelected = !!permissions[member.id];
                                            const isSelf = String(member.id) === String(loggedInUserId) && userType === 'member';
                                            const isDisabled = userType === 'member' || isSelf;
                                            return (
                                                <div key={member.id} className={`member-card ${isSelected ? 'selected' : ''}`}>
                                                    <label className="member-card-label">
                                                        <input type="checkbox" checked={isSelected || isSelf} disabled={isDisabled} onChange={(e) => toggleMember(member.id, e.target.checked)} />
                                                        {member.fname} {member.lname} {isSelf && <span className="member-self-tag">(You)</span>}
                                                        <span className="member-role-tag">({member.usertype})</span>
                                                    </label>
                                                    {isSelected && (
                                                        <div className="member-permissions-row">
                                                            <label><input type="checkbox" checked={!!permissions[member.id]?.can_view} onChange={(e) => handlePermissionChange(member.id, 'can_view', e.target.checked)} /> Can View</label>
                                                            <label><input type="checkbox" checked={!!permissions[member.id]?.can_edit} onChange={(e) => handlePermissionChange(member.id, 'can_edit', e.target.checked)} /> Can Edit</label>
                                                            <label><input type="checkbox" checked={!!permissions[member.id]?.can_delete} onChange={(e) => handlePermissionChange(member.id, 'can_delete', e.target.checked)} /> Can Delete</label>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="form-section-card">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">📝</div>
                            <div><h3>Notes / Description</h3></div>
                        </div>
                    </div>
                    <div className="description-editor">
                        <div className="description-toolbar">
                            <button type="button">B</button><button type="button">I</button><button type="button">U</button>
                        </div>
                        <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">🛒</div>
                            <div>
                                <h3>Items</h3>
                                <p>Update vehicles or items in this sale order.</p>
                            </div>
                        </div>
                        <button type="button" className="btn-add-item" onClick={addItem}>+ Add Item</button>
                    </div>

                    <div className="items-table-scroll">
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>Stock Name</th>
                                    <th>Chassis Number</th>                                    
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <select name="stock_name" value={formData.stock_name} onChange={handleChange}>
                                                <option value="">-- Select Stock --</option>
                                                {stocksData.map(stock => <option key={stock.id} value={stock.stock_name}>{stock.stock_name}</option>)}
                                            </select>
                                        </td>
                                        <td>
                                            <select name="chassis_number" value={formData.chassis_number} onChange={handleChange}>
                                                <option value="">-- Select Chassis Number --</option>
                                                {stocksData.map(stock => <option key={stock.id} value={stock.chassis_number}>{stock.chassis_number} - {stock.stock_name}</option>)}
                                            </select>
                                        </td>                                                                                
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <table className="items-table">
                            <thead>
                                <tr>                   
                                    <th>Item Name</th>                 
                                    <th>Description</th>                                   
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={index}>    
                                        <td>
                                            <select value={item.item_name} onChange={(e) => handleItemChange(index, 'item_name', e.target.value)}>
                                                <option value="">Select</option>
                                                {stocksData.map(stock => <option key={stock.id} value={stock.chassis_number}>{stock.chassis_number}</option>)}
                                            </select>
                                        </td>                                    
                                        <td><input type="text" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <table className="items-table">
                            <thead>
                                <tr>                                                   
                                    <th>Qty</th>
                                    <th>Unit Type</th>                                    
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={index}>    
                                        <td><input type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} /></td>
                                        <td><input type="text" value={item.unit_type} onChange={(e) => handleItemChange(index, 'unit_type', e.target.value)} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <table className="items-table">
                            <thead>
                                <tr>                                    
                                    <th>Unit Price</th>
                                    <th>Tax Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={index}>                                        
                                        <td><input type="number" value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} /></td>
                                        <td>
                                            <select value={item.tax_rate} onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}>
                                                <option>No Tax</option><option>5%</option><option>10%</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <table className="items-table">
                            <thead>
                                <tr>                                                                        
                                    <th className="text-center">Total</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={index}>                                                                                
                                        <td className="item-total-cell">{item.total}</td>
                                        <td className="text-center"><button type="button" className="items-table-remove-btn" onClick={() => removeItem(index)}>×</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="sale-order-bottom-area">
                    <div className="sale-order-summary-note">
                        <div className="summary-note-icon">i</div>
                        <div>
                            <strong>Terms & Conditions</strong>
                            <p>Total is calculated based on all items added. Discount & Adjustment will adjust the final amount.</p>
                        </div>
                    </div>
                    <div className="sale-order-totals">
                        <div className="total-row"><strong>Sub Total:</strong><span>{formData.sub_total}</span></div>
                        <div className="total-row"><label>Discount (%)</label><input type="number" value={formData.discount} onChange={(e) => { setFormData({ ...formData, discount: e.target.value }); calculateTotals(items); }} /></div>
                        <div className="total-row"><label>Adjustment</label><input type="number" value={formData.adjustment} onChange={(e) => { setFormData({ ...formData, adjustment: e.target.value }); calculateTotals(items); }} /></div>
                        <div className="total-row final"><span>Total:</span><span>{formData.total}</span></div>
                    </div>
                </div>

                <div className="form-submit-row">
                    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
                    <button type="submit" className="btn-blue final-save-btn" disabled={saving}>{saving ? 'Saving...' : 'Update Sale Order'}</button>
                </div>
            </form>
        </div>
    );
};

export default EditSaleForm;